import { useCallback, useRef, useState } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

interface ImageUploadZoneProps {
    onFilesSelected: (files: File[]) => void
}

export default function ImageUploadZone({ onFilesSelected }: ImageUploadZoneProps) {
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(true)
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
    }, [])

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault()
            setIsDragging(false)
            const files = Array.from(e.dataTransfer.files).filter((f) =>
                f.type.startsWith('image/')
            )
            if (files.length > 0) {
                onFilesSelected(files)
            }
        },
        [onFilesSelected]
    )

    const handleFileInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || [])
            if (files.length > 0) {
                onFilesSelected(files)
            }
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        },
        [onFilesSelected]
    )

    return (
        <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={clsx(
                'border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                'flex items-center justify-center gap-2 px-4 py-3',
                isDragging
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 bg-gray-50 hover:border-gray-400'
            )}
            onClick={() => fileInputRef.current?.click()}
        >
            <PlusIcon className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-700">Drop images here or <span className="text-primary-600 underline">browse</span></span>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileInput}
            />
        </div>
    )
}
